import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { NavigationService } from '../navigation.service';
import { InregistrareProgramareService } from '../inregistrare-programare.service';
import { ConfirmaSelectieEvent, CreateContract, Entitate, GetSlots } from '../inregistrare-programare.interface';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Generic } from 'src/app/interfaces/generic.interface';
import { catchError, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { helper } from 'src/environments/helper';
import { SpecializariService } from 'src/app/platform/nomenclatoare/activities-prog-setari/specializari/specializari.service';
import { MatCalendar } from '@angular/material/datepicker';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { PreselectieSedinta, StoreSubcontract, SubcontractLocal, SubcontractLocalSlot } from '../disponibilitate-programare/disponibilitate.interface';
import { notDefaultValidator } from 'src/app/validators/not-default';
import Swal from 'sweetalert2';
import { ActivatedRoute } from '@angular/router';

@Component({
    selector: 'app-detalii-programare',
    templateUrl: './detalii-programare.component.html',
    styleUrls: ['./detalii-programare.component.scss']
})
export class DetaliiProgramareComponent implements OnInit, OnDestroy {
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    helper = helper;

    isLoading = false;
    entitate: Entitate | null = null;
    contractId: number | null = null;
    slotsData: any = null;

    /** Dacă panoul de disponibilitate e activ (field-urile de sus sunt disabled) */
    disponibilitateActiva = false;

    private readonly DOW_RO = ['Dum', 'Lun', 'Mar', 'Mie', 'Joi', 'Vin', 'Sâm'];

    // Calendar perioada contractuala
    @ViewChild(PopoverDirective) popover!: PopoverDirective;
    @ViewChild('startCalendar') startCalendar!: MatCalendar<Date>;
    @ViewChild('endCalendar') endCalendar!: MatCalendar<Date>;

    // Calendar interval sedinte
    @ViewChild('intervalPopover') intervalPopover!: PopoverDirective;
    @ViewChild('intervalStartCalendar') intervalStartCalendar!: MatCalendar<Date>;
    @ViewChild('intervalEndCalendar') intervalEndCalendar!: MatCalendar<Date>;

    today: Date = new Date();

    // Perioada contractuala
    selectedStartDate: Date | null = null;
    selectedEndDate: Date | null = null;
    selectedLuni: number | null = null;
    luniOptions = [6, 12, 18, 24];

    // Interval sedinte
    intervalStartDate: Date | null = null;
    intervalEndDate: Date | null = null;
    intervalSelectedLuni: number | null = null;
    preluareContract: boolean = false;

    get isPerioadaCompleted(): boolean {
        return !!this.selectedStartDate && !!this.selectedEndDate;
    }

    get isContractCreated(): boolean {
        return this.contractId !== null;
    }

    infoForm: FormGroup = new FormGroup({
        date_from_contract: new FormControl('', Validators.required),
        date_end_contract: new FormControl('', Validators.required),
        nr_contract: new FormControl(''),
        data_contract: new FormControl(''),
        specializare_id: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
        nr_sedinte: new FormControl('', Validators.required),
        frecventa_sedinte: new FormControl('', Validators.required),
        date_from_sedinte: new FormControl('', Validators.required),
        date_end_sedinte: new FormControl('', Validators.required),
        timeSlots: new FormControl([], Validators.required),
    });

    specializari!: Generic[];
    private destroy$ = new Subject<void>();
    filteredSpecializari: any[] = [];
    specializariControl = new FormControl();

    initialFormValues = {
        specializare_id: -1,
    };

    noPreferinta: boolean = false;

    zile = [
        { id: 1, label: 'Luni' },
        { id: 2, label: 'Marți' },
        { id: 3, label: 'Miercuri' },
        { id: 4, label: 'Joi' },
        { id: 5, label: 'Vineri' },
    ];

    intervaleOrare = [
        { start: '09:00', end: '12:00' },
        { start: '13:00', end: '15:00' },
        { start: '15:00', end: '18:00' },
    ];

    selectedSlots: { day: number; hour_start: string; hour_end: string }[] = [];

    subcontracteLocale: SubcontractLocal[] = [];
    editingSubcontractId: number | null = null;
    preselectiiEdit: PreselectieSedinta[] = [];

    constructor(
        public navigationService: NavigationService,
        private inregistrareProgramareService: InregistrareProgramareService,
        private specializariService: SpecializariService,
        private route: ActivatedRoute,
    ) { }


    ngOnInit(): void {
        this.navigationService.currentStepIndex = 1;

        // Verificăm dacă tab-ul a fost deschis cu ?edit=1
        const isExternalEdit = this.route.snapshot.queryParamMap.get('edit') === '1';

        if (isExternalEdit) {
            const raw = sessionStorage.getItem('ext_edit_payload');

            // Consumăm și ștergem imediat indiferent de rezultat
            sessionStorage.removeItem('ext_edit_payload');

            if (raw) {
                try {
                    const { state, entitate, pendingSubcontractId } = JSON.parse(raw);

                    this.inregistrareProgramareService.setSelectedEntitateForDraft(entitate);
                    this.inregistrareProgramareService.saveDetaliiState({
                        ...state,
                        selectedStartDate: state.selectedStartDate ? new Date(state.selectedStartDate) : null,
                        selectedEndDate: state.selectedEndDate ? new Date(state.selectedEndDate) : null,
                    });
                    this.inregistrareProgramareService.setPendingEditSubcontractId(pendingSubcontractId);

                    this.entitate = this.inregistrareProgramareService.getSelectedEntitate();
                    if (!this.entitate) {
                        this.navigationService.goToStep(0);
                        return;
                    }

                    this.loadSpecializari(() => {
                        this.restoreState();
                        const pendingId = this.inregistrareProgramareService.consumePendingEditSubcontractId();
                        if (pendingId !== null) {
                            const sub = this.subcontracteLocale.find(s => s.subcontract_id === pendingId);
                            if (sub) this.editSubcontract(sub);
                        }
                    });
                    return;
                } catch (e) {
                    console.error('Error parsing ext_edit_payload:', e);
                }
            }

            // Dacă ceva a mers prost, trimitem la pasul 0
            this.navigationService.goToStep(0);
            return;
        }

        // ── Flux normal (fără ?edit=1) — sessionStorage ignorat complet ──
        this.entitate = this.inregistrareProgramareService.getSelectedEntitate();

        if (!this.entitate) {
            this.navigationService.goToStep(0);
            return;
        }

        this.loadSpecializari(() => {
            this.restoreState();
            const pendingId = this.inregistrareProgramareService.consumePendingEditSubcontractId();
            if (pendingId !== null) {
                const sub = this.subcontracteLocale.find(s => s.subcontract_id === pendingId);
                if (sub) this.editSubcontract(sub);
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ── Salvare / restaurare stare ────────────────────────────────────────────

    private saveState(): void {
        // Colectăm sloturile selectate curent din componenta disponibilitate
        // (disponibilitate le ține intern; le salvăm separat pentru delete la cancel)
        this.inregistrareProgramareService.saveDetaliiState({
            contractId: this.contractId,
            selectedStartDate: this.selectedStartDate,
            selectedEndDate: this.selectedEndDate,
            selectedLuni: this.selectedLuni,
            nrContract: this.infoForm.get('nr_contract')?.value ?? '',
            dataContract: this.infoForm.get('data_contract')?.value ?? '',
            subcontracteLocale: [...this.subcontracteLocale],
            disponibilitateActiva: this.disponibilitateActiva,
            sloturiSelectateIds: this.currentSloturiSelectate,
            fileGroups: this.inregistrareProgramareService.getDetaliiState()?.fileGroups ?? [],
        });
    }

    private restoreState(): void {
        const state = this.inregistrareProgramareService.getDetaliiState();
        if (!state) return;

        this.contractId = state.contractId;
        this.selectedStartDate = state.selectedStartDate ? new Date(state.selectedStartDate) : null;
        this.selectedEndDate = state.selectedEndDate ? new Date(state.selectedEndDate) : null;
        this.selectedLuni = state.selectedLuni;
        this.subcontracteLocale = [...state.subcontracteLocale];
        this.disponibilitateActiva = state.disponibilitateActiva ?? false;

        if (this.selectedStartDate) {
            this.infoForm.patchValue({
                date_from_contract: this.formatDateRender(this.selectedStartDate),
                date_end_contract: this.selectedEndDate ? this.formatDateRender(this.selectedEndDate) : '',
                nr_contract: state.nrContract,
                data_contract: state.dataContract,
            });
        }

        if (this.disponibilitateActiva) {
            this.setFormDisabled(true);
        }
    }

    // ── Disable/enable câmpuri disponibilitate ────────────────────────────────

    private setFormDisabled(disabled: boolean): void {
        const subFields = [
            'specializare_id', 'nr_sedinte', 'frecventa_sedinte',
            'date_from_sedinte', 'date_end_sedinte', 'timeSlots'
        ];
        subFields.forEach(f => {
            disabled
                ? this.infoForm.get(f)?.disable({ emitEvent: false })
                : this.infoForm.get(f)?.enable({ emitEvent: false });
        });
    }

    // ── Sloturi selectate curent (expuse de componenta-copil) ─────────────────

    /** Referință către sloturile selectate în disponibilitate — actualizată de evenimentul (selectiiChange) */
    currentSloturiSelectate: { slot_id: number; date: string }[] = [];

    onSelectiiChange(sloturi: { slot_id: number; date: string }[]): void {
        this.currentSloturiSelectate = sloturi;
    }

    // ── Navigare ──────────────────────────────────────────────────────────────

    handleContinua = () => {
        this.saveState();
        this.navigationService.goNext();
    }

    // ── Format date ───────────────────────────────────────────────────────────

    formatDateRender(date: Date) {
        let d = new Date(date),
            month = '' + (d.getMonth() + 1),
            day = '' + d.getDate(),
            year = d.getFullYear();

        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;

        return [year, month, day].join('-');
    }

    createContract(): void {
        if (!this.isPerioadaCompleted || !this.entitate) return;

        const payload: CreateContract = {
            entity_id: this.entitate.id,
            date_from_contract: this.formatDateRender(this.selectedStartDate!),
            date_end_contract: this.formatDateRender(this.selectedEndDate!),
            nr_contract: this.infoForm.get('nr_contract')?.value ?? '',
            data_contract: this.infoForm.get('data_contract')?.value
                ? this.formatDateRender(this.infoForm.get('data_contract')?.value)
                : '',
        };

        this.isLoading = true;
        this.inregistrareProgramareService.createContract(payload).pipe(
            finalize(() => { this.isLoading = false; })
        ).subscribe({
            next: (res: any) => {
                this.contractId = res.data.id;
            },
            error: (err) => { console.error('Error creating contract:', err); }
        });
    }

    // --- Calendar perioada contractuala ---

    get dateRangeLabel(): string {
        if (!this.selectedStartDate && !this.selectedEndDate) return 'DE LA / PÂNĂ LA';
        const start = this.selectedStartDate ? this.formatDate(this.selectedStartDate) : 'DE LA';
        const end = this.selectedEndDate ? this.formatDate(this.selectedEndDate) : 'PÂNĂ LA';
        return `${start} / ${end}`;
    }

    formatDate(date: Date): string {
        return date.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }

    onStartDateSelected(date: Date | null): void {
        if (!date) return;
        this.selectedStartDate = date;
        this.infoForm.patchValue({ date_from_contract: this.formatDateRender(date) });

        if (this.selectedLuni) {
            this.calculateEndDate(this.selectedLuni);
            this.updateCalendarView(this.endCalendar, this.selectedEndDate!);
        } else {
            if (this.selectedEndDate && this.selectedEndDate < date) {
                this.selectedEndDate = null;
                this.infoForm.patchValue({ date_end_contract: '' });
            }
            this.updateCalendarView(this.endCalendar, date);
        }

        this.validateIntervalAgainstPerioad();
    }

    onEndDateSelected(date: Date | null): void {
        if (!date) return;
        this.selectedEndDate = date;
        this.selectedLuni = null;
        this.infoForm.patchValue({ date_end_contract: this.formatDateRender(date) });
        this.validateIntervalAgainstPerioad();
    }

    selectLuni(luni: number): void {
        if (this.selectedLuni === luni) {
            this.selectedLuni = null;
            this.selectedEndDate = null;
            this.infoForm.patchValue({ date_end_contract: '' });
            this.validateIntervalAgainstPerioad();
            return;
        }

        this.selectedLuni = luni;
        if (!this.selectedStartDate) {
            this.selectedStartDate = new Date(this.today);
            this.infoForm.patchValue({ date_from_contract: this.formatDateRender(this.selectedStartDate) });
        }
        this.calculateEndDate(luni);
        this.infoForm.patchValue({ date_end_contract: this.formatDateRender(this.selectedEndDate!) });
        this.updateCalendarView(this.endCalendar, this.selectedEndDate!);
        this.validateIntervalAgainstPerioad();
        this.closePopover();
    }

    calculateEndDate(luni: number): void {
        if (!this.selectedStartDate) return;
        const end = new Date(this.selectedStartDate);
        end.setMonth(end.getMonth() + luni);
        this.selectedEndDate = end;
    }

    closePopover(): void {
        if (this.popover) this.popover.hide();
    }

    // --- Calendar interval sedinte ---

    get intervalRangeLabel(): string {
        if (!this.intervalStartDate && !this.intervalEndDate) return 'DE LA / PÂNĂ LA';
        const start = this.intervalStartDate ? this.formatDate(this.intervalStartDate) : 'DE LA';
        const end = this.intervalEndDate ? this.formatDate(this.intervalEndDate) : 'PÂNĂ LA';
        return `${start} / ${end}`;
    }

    get intervalMinStart(): Date {
        return this.selectedStartDate ?? this.today;
    }

    get intervalMaxEnd(): Date | null {
        return this.selectedEndDate;
    }

    onIntervalStartSelected(date: Date | null): void {
        if (!date) return;
        this.intervalStartDate = date;
        this.infoForm.patchValue({ date_from_sedinte: this.formatDateRender(date) });

        if (this.intervalSelectedLuni) {
            const end = new Date(date);
            end.setMonth(end.getMonth() + this.intervalSelectedLuni);

            if (this.selectedEndDate && end > this.selectedEndDate) {
                this.intervalSelectedLuni = null;
                this.intervalEndDate = null;
                this.infoForm.patchValue({ date_end_sedinte: '' });
            } else {
                this.calculateIntervalEndDate(this.intervalSelectedLuni);
                this.infoForm.patchValue({ date_end_sedinte: this.formatDateRender(this.intervalEndDate!) });
                this.updateCalendarView(this.intervalEndCalendar, this.intervalEndDate!);
            }
        } else {
            if (this.intervalEndDate && this.intervalEndDate < date) {
                this.intervalEndDate = null;
                this.infoForm.patchValue({ date_end_sedinte: '' });
            }
            this.updateCalendarView(this.intervalEndCalendar, date);
        }
    }

    onIntervalEndSelected(date: Date | null): void {
        if (!date) return;
        this.intervalEndDate = date;
        this.intervalSelectedLuni = null;
        this.infoForm.patchValue({ date_end_sedinte: this.formatDateRender(date) });
    }

    selectIntervalLuni(luni: number): void {
        if (this.intervalSelectedLuni === luni) {
            this.intervalSelectedLuni = null;
            this.intervalEndDate = null;
            this.infoForm.patchValue({ date_end_sedinte: '' });
            return;
        }

        this.intervalSelectedLuni = luni;
        if (!this.intervalStartDate) {
            this.intervalStartDate = new Date(this.intervalMinStart);
            this.infoForm.patchValue({ date_from_sedinte: this.formatDateRender(this.intervalStartDate) });
        }
        this.calculateIntervalEndDate(luni);
        this.infoForm.patchValue({ date_end_sedinte: this.formatDateRender(this.intervalEndDate!) });
        this.updateCalendarView(this.intervalEndCalendar, this.intervalEndDate!);
        this.closeIntervalPopover();
    }

    calculateIntervalEndDate(luni: number): void {
        if (!this.intervalStartDate) return;
        const end = new Date(this.intervalStartDate);
        end.setMonth(end.getMonth() + luni);

        if (this.selectedEndDate && end >= this.selectedEndDate) {
            this.intervalEndDate = new Date(this.selectedEndDate);
        } else {
            this.intervalEndDate = end;
        }
    }

    closeIntervalPopover(): void {
        if (this.intervalPopover) this.intervalPopover.hide();
    }

    validateIntervalAgainstPerioad(): void {
        if (!this.intervalStartDate && !this.intervalEndDate) return;

        if (this.selectedStartDate && this.intervalStartDate &&
            this.intervalStartDate < this.selectedStartDate) {
            this.intervalStartDate = null;
            this.intervalEndDate = null;
            this.intervalSelectedLuni = null;
            this.infoForm.patchValue({ date_from_sedinte: '', date_end_sedinte: '' });
        }

        if (this.selectedEndDate && this.intervalEndDate &&
            this.intervalEndDate > this.selectedEndDate) {
            this.intervalEndDate = null;
            this.intervalSelectedLuni = null;
            this.infoForm.patchValue({ date_end_sedinte: '' });
        }
    }

    // --- Shared ---

    updateCalendarView(calendar: MatCalendar<Date>, date: Date): void {
        if (calendar && date) {
            calendar.activeDate = new Date(date);
            calendar.updateTodaysDate();
        }
    }

    // --- Specializari ---

    resetForm() {
        this.infoForm.reset(this.initialFormValues);
        this.filteredSpecializari = this.specializari;
        this.specializariControl.setValue(null);
        this.destroy$.next();
        this.destroy$.complete();
        this.renderFieldsSearch();
    }

    renderFieldsSearch() {
        this.specializariControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredSpecializari = this.specializari;
                    return of(this.specializari);
                }
                return this.filterSpecializari(inputValue);
            }),
            tap((data: any[]) => { this.filteredSpecializari = data; }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    loadSpecializari(callback?: () => void) {
        this.isLoading = true;
        this.specializariService.listActive().pipe(
            map((results: any) => [...results.data]),
            finalize(() => { this.isLoading = false; })
        ).subscribe({
            next: (results) => {
                this.specializari = results;
                this.filteredSpecializari = results;
                if (callback) callback();
            },
            error: (err) => { console.error('Error in get specializari active:', err); }
        });
    }

    filterSpecializari(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);
        const filtered = this.specializari.filter(s =>
            this.normalizeString(s.name).includes(normalizedSearchTerm)
        );
        return of(filtered);
    }

    onSpecializareSelectClosed() {
        const selectedValue = this.specializariControl.value;
        if (selectedValue) {
            const isValid = this.filteredSpecializari.some(s =>
                s.id === this.infoForm.get('specializare_id')?.value
            );
            if (!isValid) {
                this.specializariControl.setValue(null);
                this.filteredSpecializari = this.specializari;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    selectFrecventa(nr: number) {
        this.infoForm.get('frecventa_sedinte')?.patchValue(nr);
    }

    onPreluareContractChange(): void {
        if (this.preluareContract) {
            this.intervalStartDate = this.selectedStartDate ? new Date(this.selectedStartDate) : null;
            this.intervalEndDate = this.selectedEndDate ? new Date(this.selectedEndDate) : null;
            this.intervalSelectedLuni = this.selectedLuni;

            this.infoForm.patchValue({
                date_from_sedinte: this.intervalStartDate ? this.formatDateRender(this.intervalStartDate) : '',
                date_end_sedinte: this.intervalEndDate ? this.formatDateRender(this.intervalEndDate) : '',
            });
        } else {
            this.intervalStartDate = null;
            this.intervalEndDate = null;
            this.intervalSelectedLuni = null;
            this.infoForm.patchValue({ date_from_sedinte: '', date_end_sedinte: '' });
        }
    }

    get availableLuniOptions(): number[] {
        if (!this.selectedStartDate || !this.selectedEndDate) {
            return this.luniOptions;
        }

        const baseDate = this.intervalStartDate ?? this.selectedStartDate;

        return this.luniOptions.filter(luni => {
            const end = new Date(baseDate!);
            end.setMonth(end.getMonth() + luni);
            return end <= this.selectedEndDate!;
        });
    }

    get maxSlots(): number {
        return this.infoForm.get('frecventa_sedinte')?.value ?? 1;
    }

    isSlotSelected(day: number, interval: { start: string; end: string }): boolean {
        return this.selectedSlots.some(
            s => s.day === day && s.hour_start === interval.start && s.hour_end === interval.end
        );
    }

    toggleSlot(day: number, interval: { start: string; end: string }): void {
        const idx = this.selectedSlots.findIndex(
            s => s.day === day && s.hour_start === interval.start && s.hour_end === interval.end
        );

        if (idx !== -1) {
            this.selectedSlots.splice(idx, 1);
        } else {
            if (this.selectedSlots.length >= this.maxSlots) {
                this.selectedSlots.shift();
            }
            this.selectedSlots.push({ day, hour_start: interval.start, hour_end: interval.end });
        }

        this.infoForm.patchValue({ timeSlots: [...this.selectedSlots] });
    }

    onNoPreferintaChange(): void {
        if (this.noPreferinta) {
            this.selectedSlots = [];
            this.infoForm.patchValue({ timeSlots: [] });
            this.infoForm.get('timeSlots')?.clearValidators();
        } else {
            this.infoForm.get('timeSlots')?.setValidators(Validators.required);
        }
        this.infoForm.get('timeSlots')?.updateValueAndValidity();
    }

    // ── Arată disponibilitate ─────────────────────────────────────────────────

    arataDisponibilitate(): void {
        if (!this.entitate || !this.contractId) return;

        // ── Verificare: dacă suntem în modul editare și nrSedinte a scăzut ──────────
        const nrSedinte = this.infoForm.getRawValue().nr_sedinte;
        const subcontractulEditat = this.editingSubcontractId !== null
            ? this.subcontracteLocale.find(s => s.subcontract_id === this.editingSubcontractId)
            : null;

        if (subcontractulEditat) {
            const nrSedintePrecedente = subcontractulEditat.timeSlots.length;

            if (nrSedinte < nrSedintePrecedente) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Număr de ședințe redus',
                    html: `
                    Ai redus numărul de ședințe de la <strong>${nrSedintePrecedente}</strong>
                    la <strong>${nrSedinte}</strong>.<br><br>
                    Sunt deja <strong>${nrSedintePrecedente}</strong> ședințe selectate.
                    Va trebui să elimini <strong>${nrSedintePrecedente - nrSedinte}</strong>
                    ședință/ședințe din cele existente înainte de a putea salva programarea.
                `,
                    confirmButtonText: 'Am înțeles, continuă',
                    cancelButtonText: 'Anulează',
                    showCancelButton: true,
                    confirmButtonColor: '#1976d2',
                    cancelButtonColor: '#9e9e9e',
                    reverseButtons: true,
                }).then(result => {
                    if (result.isConfirmed) {
                        this.doArataDisponibilitate();
                    }
                });
                return;
            }
        }

        this.doArataDisponibilitate();
    }

    private doArataDisponibilitate(): void {
        const payload: GetSlots = {
            id: this.contractId!,
            ...this.infoForm.getRawValue(),
            entity_id: this.entitate!.id,
        };

        this.isLoading = true;
        this.inregistrareProgramareService.getSlots(payload).pipe(
            finalize(() => { this.isLoading = false; })
        ).subscribe({
            next: (res: any) => {
                this.slotsData = res;
                this.disponibilitateActiva = true;
                this.setFormDisabled(true);
                this.currentSloturiSelectate = [];
            },
            error: (err) => {
                console.error('Error getting slots:', err);
            }
        });
    }

    // ── Anulează disponibilitate ──────────────────────────────────────────────

    anuleazaDisponibilitate(): void {
        const sloturiDeAnulat = [...this.currentSloturiSelectate];

        // Dacă avem sloturi selectate, le ștergem pe backend
        if (sloturiDeAnulat.length > 0 && this.contractId) {
            const payload = {
                contract_id: this.contractId,
                sedinte: sloturiDeAnulat.map(s => ({ slot_id: s.slot_id, date: s.date, action: 'delete' }))
            };
            this.inregistrareProgramareService.updateSlots(payload as any).subscribe({
                error: err => console.error('Error updateSlots on cancel:', err)
            });
        }

        // Resetăm starea disponibilitate
        this.slotsData = null;
        this.disponibilitateActiva = false;
        this.currentSloturiSelectate = [];
        this.setFormDisabled(false);
    }

    // ── Confirmare selecție ───────────────────────────────────────────────────

    handleConfirmaSelectie(event: ConfirmaSelectieEvent): void {
        if (!this.entitate || !this.contractId) return;

        const form = this.infoForm.getRawValue(); // getRawValue include și câmpurile disabled

        const payload: StoreSubcontract = {
            contract_id: this.contractId,
            date_from_contract: form.date_from_contract,
            date_end_contract: form.date_end_contract,
            nr_contract: form.nr_contract ?? '',
            data_contract: form.data_contract ?? '',
            specializare_id: form.specializare_id,
            nr_sedinte: form.nr_sedinte,
            frecventa_sedinte: form.frecventa_sedinte,
            date_from_sedinte: form.date_from_sedinte,
            date_end_sedinte: form.date_end_sedinte,
            timeSlots: event.timeSlots.map(s => ({ slot_id: s.slot_id, date: s.date })),
            entity_id: this.entitate.id,
            preferredTimeSlots: this.noPreferinta ? [] : [...this.selectedSlots],
        };

        this.isLoading = true;

        const isEdit = this.editingSubcontractId !== null;
        const req$ = isEdit
            ? this.inregistrareProgramareService.updateSubcontract(this.editingSubcontractId!, payload)
            : this.inregistrareProgramareService.storeSubcontract(payload);

        req$.pipe(finalize(() => { this.isLoading = false; })).subscribe({
            next: (res: any) => {
                const subcontractId = isEdit ? this.editingSubcontractId! : res.data.id;

                const specializareName = this.specializari.find(
                    s => s.id === form.specializare_id
                )?.name ?? '';

                const subcontractLocal: SubcontractLocal = {
                    subcontract_id: subcontractId,
                    specializare_id: form.specializare_id,
                    specializare_name: specializareName,
                    nr_sedinte: form.nr_sedinte,
                    frecventa_sedinte: form.frecventa_sedinte,
                    date_from_sedinte: form.date_from_sedinte,
                    date_end_sedinte: form.date_end_sedinte,
                    timeSlots: event.timeSlots,
                    nr_contract: form.nr_contract ?? '',
                    data_contract: form.data_contract ?? '',
                    date_from_contract: form.date_from_contract,
                    date_end_contract: form.date_end_contract,
                    preferinteOrare: [...this.selectedSlots],
                    preluareContract: this.preluareContract,
                    noPreferinta: this.noPreferinta,
                };

                if (isEdit) {
                    const idx = this.subcontracteLocale.findIndex(
                        s => s.subcontract_id === this.editingSubcontractId
                    );
                    if (idx !== -1) this.subcontracteLocale[idx] = subcontractLocal;
                } else {
                    this.subcontracteLocale.push(subcontractLocal);
                }

                this.editingSubcontractId = null;
                this.resetDupaStore();
            },
            error: (err) => { console.error('Error store/update subcontract:', err); }
        });
    }

    // ─── editSubcontract ───────────────────────────────────────────────────────

    editSubcontract(sub: SubcontractLocal): void {
        this.editingSubcontractId = sub.subcontract_id;

        this.infoForm.patchValue({
            specializare_id: sub.specializare_id,
            nr_sedinte: sub.nr_sedinte,
            frecventa_sedinte: sub.frecventa_sedinte,
            date_from_sedinte: sub.date_from_sedinte,
            date_end_sedinte: sub.date_end_sedinte,
            timeSlots: sub.preferinteOrare,
        });

        this.intervalStartDate = sub.date_from_sedinte ? new Date(sub.date_from_sedinte) : null;
        this.intervalEndDate = sub.date_end_sedinte ? new Date(sub.date_end_sedinte) : null;
        this.intervalSelectedLuni = null;
        this.preluareContract = sub.preluareContract;

        this.selectedSlots = [...sub.preferinteOrare];
        this.noPreferinta = sub.noPreferinta;

        if (this.noPreferinta) {
            this.infoForm.get('timeSlots')?.clearValidators();
            this.infoForm.get('timeSlots')?.updateValueAndValidity();
        }

        this.preselectiiEdit = sub.timeSlots.map(s => ({
            slot_id: s.slot_id,
            date: s.date,
            time_from: s.time_from,
            specialist_id: s.specialist_id,
            nume_specialist: s.nume_specialist,
        }));

        this.slotsData = null;
        this.disponibilitateActiva = false;
        this.currentSloturiSelectate = [];
        this.setFormDisabled(false);
        this.arataDisponibilitate();

        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // ─── deleteSubcontract ─────────────────────────────────────────────────────

    deleteSubcontract(sub: SubcontractLocal): void {
        this.isLoading = true;
        this.inregistrareProgramareService.deleteSubcontract(sub.subcontract_id).pipe(
            finalize(() => { this.isLoading = false; })
        ).subscribe({
            next: () => {
                this.subcontracteLocale = this.subcontracteLocale.filter(
                    s => s.subcontract_id !== sub.subcontract_id
                );
            },
            error: (err) => { console.error('Error deleteSubcontract:', err); }
        });
    }

    cancelEdit(): void {
        this.editingSubcontractId = null;
        this.preselectiiEdit = [];
        this.resetDupaStore();
    }

    resetDupaStore(): void {
        const dateFromContract = this.infoForm.getRawValue().date_from_contract;
        const dateEndContract = this.infoForm.getRawValue().date_end_contract;
        const nrContract = this.infoForm.getRawValue().nr_contract;
        const dataContract = this.infoForm.getRawValue().data_contract;

        this.infoForm.reset();
        this.infoForm.patchValue({
            date_from_contract: dateFromContract,
            date_end_contract: dateEndContract,
            nr_contract: nrContract,
            data_contract: dataContract,
            specializare_id: -1,
        });

        this.preselectiiEdit = [];
        this.slotsData = null;
        this.disponibilitateActiva = false;
        this.currentSloturiSelectate = [];
        this.setFormDisabled(false);

        this.selectedSlots = [];
        this.noPreferinta = false;
        this.intervalStartDate = null;
        this.intervalEndDate = null;
        this.intervalSelectedLuni = null;
        this.preluareContract = false;

        this.filteredSpecializari = this.specializari;
        this.specializariControl.setValue(null);
        this.destroy$.next();
        this.destroy$.complete();
        this.renderFieldsSearch();
    }

    getSpecialistiDinSubcontract(sub: SubcontractLocal): { nume: string; slots: SubcontractLocalSlot[] }[] {
        const map = new Map<string, SubcontractLocalSlot[]>();

        for (const slot of sub.timeSlots) {
            const key = slot.nume_specialist;
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(slot);
        }

        return Array.from(map.entries()).map(([nume, slots]) => ({
            nume,
            slots: slots.sort((a, b) => a.date.localeCompare(b.date))
        }));
    }

    formatSlotTime(slot: SubcontractLocalSlot): string {
        return slot.time_from ? slot.time_from.substring(0, 5) : '';
    }

    getDayOfWeekRo(dateStr: string): string {
        const d = new Date(dateStr + 'T00:00:00');
        return this.DOW_RO[d.getDay()];
    }

    handleAnulare(): void {
        const contractId = this.contractId;
        if (!contractId) {
            this.inregistrareProgramareService.resetAll();
            this.navigationService.goToStep(0);
            return;
        }

        Swal.fire({
            icon: 'warning',
            title: 'Anulare contract',
            text: 'Ești sigur că vrei să anulezi? Contractul și toate programările asociate vor fi șterse.',
            showCancelButton: true,
            confirmButtonText: 'Da, anulează',
            cancelButtonText: 'Înapoi',
            confirmButtonColor: '#d32f2f',
            cancelButtonColor: '#9e9e9e',
            reverseButtons: true,
        }).then(result => {
            if (!result.isConfirmed) return;

            this.isLoading = true;
            this.inregistrareProgramareService.deleteContract(contractId).pipe(
                finalize(() => { this.isLoading = false; })
            ).subscribe({
                next: () => {
                    this.inregistrareProgramareService.resetAll();
                    this.navigationService.goToStep(0);
                },
                error: (err) => { console.error('Error deleteContract:', err); }
            });
        });
    }
}